import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Crumbs, useKeepStore } from "@/components/layout/Crumbs";
import { LandingHead } from "@/components/shop/GroupCard";
import { Fold, FoldBody } from "@/components/layout/Fold";
import { Reveal } from "@/components/Reveal";
import api, { mediaUrl } from "@/lib/api";
import { useMall } from "@/context/MallContext";
import { useShop } from "@/lib/useShop";
import { money } from "@/components/simple/product/Fields";
import Mockup from "./mockups/Mockup";

/** One print product: the photo (or the flat mockup), the name, "from $X", the ready date. Opens the designer. */
function PrintTile({ tile, theme, keep }) {
    return (
        <Link to={keep(`/print/${tile.key}`)} data-testid={`print-tile-${tile.key}`}
            className="group flex flex-col border-2 border-zinc-900 bg-white transition-transform duration-200 hover:-translate-y-1">
            <div className="relative aspect-square overflow-hidden border-b-2 border-zinc-900 bg-zinc-100">
                {tile.image
                    ? <img src={mediaUrl(tile.image)} alt={tile.label} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
                    : <Mockup kind={tile.mockup} className="absolute inset-0 h-full w-full" />}
                {tile.variants.length > 1 && (
                    <span className="absolute left-0 top-0 bg-white px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-zinc-900">
                        {tile.variants.length} options
                    </span>
                )}
            </div>
            <div className="flex flex-1 items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                    <h3 className="font-display text-base font-black uppercase leading-tight tracking-tight text-zinc-900">{tile.label}</h3>
                    <p className="mt-0.5 text-sm text-zinc-600" data-testid={`print-tile-from-${tile.key}`}>from {money(tile.from_price)}{tile.ready?.text ? ` · ${tile.ready.text}` : ""}</p>
                </div>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center transition-transform duration-200 group-hover:translate-x-1" style={{ background: theme.primary, color: theme.on || "#fff" }}>
                    <ArrowRight size={16} />
                </span>
            </div>
        </Link>
    );
}

/** FLYERS landing (Phase 10): every paper product this shop sells, priced live from the trade printer. A tile opens the designer. */
export default function PrintLanding() {
    const { shopSlug } = useShop();
    const { theme, shown: store } = useMall();
    const keep = useKeepStore();
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!shopSlug) return;
        setData(null);
        api.get("/print/products", { params: { store: shopSlug } }).then(({ data }) => setData(data)).catch(() => setData({ tiles: [] }));
    }, [shopSlug]);

    const tiles = (data?.tiles || []).filter((t) => !t.on_signs);

    return (
        <Layout title="Flyers & Print">
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="print-landing">
                <Reveal>
                    <Crumbs trail={[{ label: "Flyers & Print" }]} />
                    <div className="mt-4">
                        <LandingHead testid="print-head" kicker={<span data-testid="print-kicker">Flyers, cards, menus, stickers and more · most ship in 3 business days</span>}
                            title={<span data-testid="print-title">Pick a product.</span>}
                            line={<span data-testid="print-store-note">{store ? <>Sold by <span className="font-semibold text-zinc-900">{store.name}</span>. </> : null}Every size, paper, coating and quantity the trade printer offers, priced live as you pick. Upload your file, see the proof, pay.</span>} />
                    </div>
                </Reveal>
                {data === null ? (
                    <div className="flex min-h-[40vh] items-center justify-center text-zinc-400" data-testid="print-loading"><Loader2 className="animate-spin" /></div>
                ) : tiles.length === 0 ? (
                    <p className="mt-8 text-zinc-600" data-testid="print-empty">This shop doesn't sell print products yet.</p>
                ) : (
                    <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="print-tiles">
                        {tiles.map((t, i) => <Reveal key={t.key} delay={Math.min(i, 8) * 0.04}><PrintTile tile={t} theme={theme} keep={keep} /></Reveal>)}
                    </div>
                )}
            </section>
            <Fold title="How print pricing works — paper, coating, quantity, turnaround" kicker="The fine print" testid="print-fold-about">
                <FoldBody>
                    <div className="grid gap-6 text-base text-zinc-700 md:grid-cols-3">
                        <p><span className="font-semibold text-zinc-900">Live prices.</span> Every option you pick — size, paper stock, coating, sides, quantity, turnaround — is priced for that exact selection the moment you pick it. No quote emails.</p>
                        <p><span className="font-semibold text-zinc-900">Your file is the proof.</span> Upload a PDF, PNG or JPG and it lands on the product so you can see what you're buying. We check the resolution and tell you if it may print soft.</p>
                        <p><span className="font-semibold text-zinc-900">Turnaround is production time.</span> "Ready to ship in 3 business days" means it leaves the press three business days after your file is approved. Pickup or shipping is on top.</p>
                    </div>
                </FoldBody>
            </Fold>
        </Layout>
    );
}
